import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})

export class LogisticsService {
   apiURL: string = environment.apiURL;

   // Shared date state for logistics components (except archive)
   private readonly selectedDateSubject: BehaviorSubject<Date> = new BehaviorSubject<Date>(new Date());
   public readonly selectedDate$: Observable<Date> = this.selectedDateSubject.asObservable();

   constructor(private http: HttpClient) {}

   /**
    * Get the current selected date
    */
   getSelectedDate(): Date {
     return this.selectedDateSubject.value;
   }

   /**
    * Update the selected date and notify all subscribers
    */
   setSelectedDate(date: Date): void {
     this.selectedDateSubject.next(date);
   }

   /**
    * Create delivery plan for choosen date
    * @param data
    */
   createDeliveryPlan(data): Observable<any> {
    return this.http.post(`${this.apiURL}/delivery-plans`, data);
   }

   /**
    * Get delivery plan by delivery date
    */
   getDeliveryPlan(deliveryDeadline: string): Observable<any> {
    let params = new HttpParams();

    if (deliveryDeadline) params = params.append('deliveryDeadline', deliveryDeadline);

    return this.http.get(`${this.apiURL}/delivery-plans`, { params });
   }

   /**
   * Download delivery plan by date
   */
  downloadDeliveryPlan(deliveryDeadline: string): Observable<any> {
    let params = new HttpParams();

    if (deliveryDeadline) params = params.append('deliveryDeadline', deliveryDeadline);

    return this.http.get(`${this.apiURL}/delivery-plans/download`, { params, responseType: 'blob', observe: 'body' });
  }

  /**
   * Set work orders cargo (pack work orders)
   * @param data 
   */
  setWorkOrdersCargo(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/cargos`, data);
  }

  /**
   * Get list of transporters
   */
  getTransporters(search?: string): Observable<any> {

    return this.http.get(`${this.apiURL}/transporters?search=${search}`);
  }

  /**
   * Create bill of lading
   * @param data
   */
  createBillOfLading(data: {workOrderIds: Array<string>, transporterId: string, deliveryPlanId: string}): Observable<any> {
    return this.http.post(`${this.apiURL}/bills-of-lading`, data);
  }

  /**
   * Get list of bills of lading
   */
  getBillOfLadingList(data: {deliveryDeadline?: string, fromDate?: string, toDate?: string, shippingMethod?: string}): Observable<any> {
    let params = new HttpParams();

    if (data.deliveryDeadline) params = params.append('deliveryDeadline', data.deliveryDeadline);
    if (data.fromDate) params = params.append('fromDate', data.fromDate);
    if (data.toDate) params = params.append('toDate', data.toDate);
    if (data.shippingMethod) params = params.append('shippingMethod', data.shippingMethod);

    return this.http.get(`${this.apiURL}/bills-of-lading`, { params });
  }

  /**
   * Download bill of lading
   */
  downloadBillOfLading(billOfLadingId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/bills-of-lading/${billOfLadingId}/download`, { responseType: 'blob', observe: 'body' });
  }

  /**
   * Set work order shipping method on delivery plan list
   * @param deliveryPlanId
   * @param data
   */
  setWorkOrderShippingMethod(deliveryPlanId: string, data: {workOrderIds: Array<string>, shippingMethod: string}): Observable<any> {
    return this.http.patch(`${this.apiURL}/delivery-plans/${deliveryPlanId}/shipping-method`, data);
  }

  /**
   * Remove work order from delivery plan
   */
  removeDeliveryPlanWorkOrder(deliveryPlanId: string, workOrderId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/delivery-plans/${deliveryPlanId}/work-orders/${workOrderId}`);
  }

  /**
   * Edit bill of lading tranporter
   */
  editBillOfLadingTransporter(billOfLadingId: string, transporterId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/bills-of-lading/${billOfLadingId}/transporters/${transporterId}`, {});
  }

  /**
   * Get list of work orders for delivery plan
   */
  getDeliveryPlanWorkOrders(deliveryDeadline: string): Observable<any> {
    let params = new HttpParams();

    if (deliveryDeadline) params = params.append('deliveryDeadline', deliveryDeadline);

    return this.http.get(`${this.apiURL}/delivery-plans/work-orders`, { params });
  }

  /**
   * Add new work order to delivery plan
   */
  addWorkOrderToDeliveryPlan(deliveryPlanId: string, workOrderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/delivery-plans/${deliveryPlanId}/work-order`, { workOrderId });
  }

  /**
   * Detach work order from cargo (the work order remains in the delivery plan, but is no longer linked to this cargo)
   */
  removeWorkOrderFromCargo(cargoId: string, workOrderId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/cargos/${cargoId}/work-orders/${workOrderId}`);
  }

  /**
   * Get available work orders for adding to cargo
   */
  getListOfWorkOrdersForCargo(deliveryDeadline: string): Observable<any> {
    let params = new HttpParams();

    if (deliveryDeadline) params = params.append('deliveryDeadline', deliveryDeadline);

    return this.http.get(`${this.apiURL}/cargos/work-orders/`, { params });
  }

  /**
   * Add work order to cargo (additional)
   */
  addWorkOrderToCargo(cargoId: string, data: {workOrderId: string, billOfLadingId: string}): Observable<any> {
    return this.http.post(`${this.apiURL}/cargos/${cargoId}/work-orders`, data);
  }

  /**
   * Edit cargo
   */
  editCargo(cargoId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/cargos/${cargoId}`, data);
  }
}